import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  Building2, 
  Plus, 
  Search, 
  Filter,
  Edit2,
  Trash2,
  Shield,
  RefreshCw,
  Users,
  Activity
} from 'lucide-react';
import { useIntlFormatting } from '../../utils/intl';
import { useAuth } from '../../contexts/AuthContext';
import { getRequestAuthToken } from '../../auth/appliedAuthToken';

interface Organization {
  id: string;
  name: string;
  type: string;
  billing_plan: string;
  status: string;
  branding?: {
    logo_url?: string;
    primary_color?: string;
  };
  created_at: string;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  pages: number;
}

interface OrgsResponse {
  orgs: Organization[];
  pagination: Pagination;
}

const AdminOrgs: React.FC = () => {
  const { t } = useTranslation(['admin_orgs', 'admin_common', 'common']);
  const { formatDate } = useIntlFormatting();
  const { user: currentUser } = useAuth();
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    page_size: 20,
    total: 0,
    pages: 0
  });

  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // UI State
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Check admin access
  if (currentUser?.role !== 'superadmin') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('admin_common:accessDenied')}</h1>
          <p className="text-gray-600">{t('admin_common:superadminRequired')}</p>
        </div>
      </div>
    );
  }

  // Fetch organizations
  const fetchOrgs = async () => {
    try {
      setLoading(true);
      const token = getRequestAuthToken();
      if (!token) {
        // No guarded token (AWS lag / logged out): never issue `Bearer null`.
        setLoading(false);
        return;
      }
      const params = new URLSearchParams();
      
      if (searchQuery) params.append('q', searchQuery);
      if (statusFilter) params.append('status', statusFilter);
      params.append('page', pagination.page.toString());
      params.append('page_size', pagination.page_size.toString());

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/orgs?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data: OrgsResponse = await response.json();
        setOrgs(data.orgs);
        setPagination(data.pagination);
      } else {
        console.error('Failed to fetch organizations');
      }
    } catch (error) {
      console.error('Error fetching organizations:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrgs();
  }, [pagination.page, searchQuery, statusFilter]);

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'coop': return 'bg-blue-100 text-blue-800';
      case 'farm': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'bg-green-100 text-green-800';
      case 'suspended': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getBillingPlanColor = (plan: string) => {
    switch (plan) {
      case 'premium': return 'bg-purple-100 text-purple-800';
      case 'pro': return 'bg-blue-100 text-blue-800';
      case 'basic': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Building2 className="w-8 h-8 text-green-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{t('admin_orgs:title')}</h1>
            <p className="text-gray-600">{t('admin_orgs:subtitle')}</p>
          </div>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`p-2 rounded-lg border ${showFilters ? 'bg-green-50 border-green-200' : 'border-gray-300'} hover:bg-gray-50 transition-colors`}
          >
            <Filter className="w-5 h-5" />
          </button>
          <button
            onClick={() => setIsCreateModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            <Plus className="w-5 h-5" />
            <span>{t('admin_orgs:actions.create')}</span>
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder={t('admin_orgs:search.placeholder')}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
          />
        </div>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-gray-50 rounded-lg p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('admin_orgs:filters.status')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
              >
                <option value="">{t('admin_orgs:filters.allStatuses')}</option>
                <option value="active">{t('admin_orgs:status.active')}</option>
                <option value="suspended">{t('admin_orgs:status.suspended')}</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => {
                  setStatusFilter('');
                  setSearchQuery('');
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                {t('common:reset')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Organizations Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {loading ? (
          <div className="col-span-full flex items-center justify-center h-64">
            <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : (
          orgs.map((org) => (
            <div key={org.id} className="bg-white rounded-lg border border-gray-200 p-6 hover:shadow-lg transition-shadow">
              {/* Organization Header */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  {org.branding?.logo_url ? (
                    <img 
                      src={org.branding.logo_url} 
                      alt={org.name}
                      className="w-10 h-10 rounded-lg object-cover"
                    />
                  ) : (
                    <div 
                      className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-semibold"
                      style={{ backgroundColor: org.branding?.primary_color || '#10B981' }}
                    >
                      {org.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <h3 className="font-semibold text-gray-900">{org.name}</h3>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getTypeColor(org.type)}`}>
                        {t(`admin_orgs:types.${org.type}`)}
                      </span>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(org.status)}`}>
                        {t(`admin_orgs:status.${org.status}`)}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {setSelectedOrg(org); setIsEditModalOpen(true);}}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 rounded-lg"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>

              {/* Organization Details */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('admin_orgs:fields.billingPlan')}</span>
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${getBillingPlanColor(org.billing_plan)}`}>
                    {t(`admin_orgs:billingPlans.${org.billing_plan}`)}
                  </span>
                </div>
                
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">{t('admin_orgs:fields.created')}</span>
                  <span className="text-sm text-gray-900">
                    {formatDate(org.created_at)}
                  </span>
                </div>

                {/* Quick Stats Placeholders */}
                <div className="pt-3 border-t border-gray-100">
                  <div className="flex items-center justify-between text-sm">
                    <div className="flex items-center space-x-1">
                      <Users className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{t('admin_orgs:stats.users')}</span>
                    </div>
                    <span className="text-gray-900 font-medium">--</span>
                  </div>
                  <div className="flex items-center justify-between text-sm mt-2">
                    <div className="flex items-center space-x-1">
                      <Activity className="w-4 h-4 text-gray-400" />
                      <span className="text-gray-600">{t('admin_orgs:stats.devices')}</span>
                    </div>
                    <span className="text-gray-900 font-medium">--</span>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-2 mt-4 pt-4 border-t border-gray-100">
                <button className="flex-1 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors">
                  {t('admin_orgs:actions.viewUsers')}
                </button>
                <button className="flex-1 px-3 py-2 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors">
                  {t('admin_orgs:actions.viewDevices')}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-white rounded-lg border border-gray-200 mt-6 px-6 py-3">
          <div className="flex items-center justify-between">
            <div className="text-sm text-gray-700">
              {t('admin_common:pagination.showing', {
                start: (pagination.page - 1) * pagination.page_size + 1,
                end: Math.min(pagination.page * pagination.page_size, pagination.total),
                total: pagination.total
              })}
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setPagination(p => ({...p, page: Math.max(1, p.page - 1)}))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                {t('common:previous')}
              </button>
              <span className="px-3 py-1">
                {pagination.page} / {pagination.pages}
              </span>
              <button
                onClick={() => setPagination(p => ({...p, page: Math.min(p.pages, p.page + 1)}))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border border-gray-300 rounded-lg disabled:opacity-50"
              >
                {t('common:next')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Placeholder for Create/Edit Modals */}
      {isCreateModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">{t('admin_orgs:create.title')}</h2>
            <p className="text-gray-600 mb-4">{t('admin_orgs:create.placeholder')}</p>
            <button
              onClick={() => setIsCreateModalOpen(false)}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg"
            >
              {t('common:close')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrgs;